import { Fragment, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useReconnecting } from './api.ts';
import { QrCode } from './codes.tsx';
import { useLevel } from './scope.ts';
import { Heading } from './screen.tsx';
import { strings } from './strings.ts';
import { UiBadge, type BadgeTone } from './ui/badge.tsx';

/**
 * What a page shows inside its sections: a loading line, columns, counts
 * as bars, label and value pairs, a table, a chip, a ticket (a value stub
 * beside its terms) and a disclosure; and the reconnecting line over every page. Each takes words and data; none takes
 * a class.
 */

export function Spinner() {
  return <span className="inline-block size-s4 shrink-0 animate-spin rounded-chip border-2 border-current border-r-transparent" aria-hidden="true" />;
}

/** A wait, said in words: `role="status"`, so a screen reader hears it. */
export function Loading({ label }: { readonly label: string }) {
  return (
    <p className="flex items-center gap-s2 text-ink-2" role="status">
      <Spinner />
      {label}
    </p>
  );
}

/** One calm line over every page while a command or a read waits to retry (kit/api.ts), in place of an error; nothing while none does. */
export function Reconnecting() {
  if (!useReconnecting()) return null;
  return (
    <p className="fixed inset-x-0 top-s4 z-40 mx-auto flex w-fit items-center gap-s2 rounded-chip border border-line bg-surface px-s4 py-s2 text-ink-2 shadow-float print:hidden" role="status" data-testid="reconnecting">
      <Spinner />
      {strings.common.reconnecting}
    </p>
  );
}

/** Two columns from a desk's width, one on a phone; each column a stack. */
export function Columns({ children }: { readonly children: readonly [ReactNode, ReactNode] }) {
  return (
    <div className="grid grid-cols-1 items-start gap-s4 desk:grid-cols-2">
      <div className="flex flex-col gap-s4">{children[0]}</div>
      <div className="flex flex-col gap-s4">{children[1]}</div>
    </div>
  );
}

/** Counts as bars against the largest (or `max`): label, bar, number, the number written by `count` (the page's MarketFormat). None says `empty`. */
export function Bars({ items, max, count, empty }: { readonly items: readonly { readonly label: string; readonly value: number }[]; readonly max?: number; readonly count: (value: number) => string; readonly empty: string }) {
  if (items.length === 0) return <p className="text-muted">{empty}</p>;
  const top = Math.max(1, max ?? Math.max(...items.map((item) => item.value)));
  return (
    <ul className="m-0 flex list-none flex-col gap-s3 p-0">
      {items.map((item) => (
        <li key={item.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-s3 gap-y-s1">
          <span className="min-w-0 [overflow-wrap:anywhere]">{item.label}</span>
          <span className="font-semibold tabular-nums">{count(item.value)}</span>
          <span className="col-span-2 h-s2 overflow-hidden rounded-chip bg-sunk" aria-hidden="true">
            {/* No fill for zero; a small count still shows. */}
            {item.value > 0 && <span className="block h-full min-w-s1 rounded-chip bg-brand" style={{ width: `${String(Math.round((item.value / top) * 100))}%` }} />}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Label and value pairs, one per line, the value at the end. */
export function Facts({ items }: { readonly items: readonly { readonly label: string; readonly value: ReactNode }[] }) {
  return (
    <dl className="m-0 flex flex-col gap-s2 border-t border-line pt-s3">
      {items.map((item) => (
        <div key={item.label} className="flex justify-between gap-s3">
          <dt className="text-ink-2">{item.label}</dt>
          <dd className="m-0 font-semibold tabular-nums">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A table cell: its words, muted (nothing to show) or fixed-width (a phone number), and a quieter line under them. */
export interface Cell {
  readonly text: ReactNode;
  readonly tone?: 'muted' | 'figures';
  readonly note?: string;
  readonly testId?: string;
}
const CELL = { muted: 'text-muted', figures: 'tabular-nums whitespace-nowrap' } as const;

/**
 * Rows of a list under their column labels on a desk; on a phone each row
 * is a card of label and value lines.
 */
export function Table<Row>({ columns, rows, keyOf, rowTestId }: { readonly columns: readonly { readonly label: string; readonly cell: (row: Row) => Cell }[]; readonly rows: readonly Row[]; readonly keyOf: (row: Row) => string; readonly rowTestId?: string }) {
  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <table className="w-full border-collapse text-left">
        <thead className="hidden desk:table-header-group">
          <tr className="border-b border-line bg-sunk">
            {columns.map((column) => (
              <th key={column.label} scope="col" className="h-s7 px-s4 text-caption font-semibold whitespace-nowrap text-muted">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={keyOf(row)} data-testid={rowTestId} className="flex flex-col gap-s1 border-b border-line p-s4 last:border-b-0 desk:table-row desk:p-0">
              {columns.map((column) => {
                const cell = column.cell(row);
                return (
                  <td key={column.label} data-label={column.label} data-testid={cell.testId} className={`flex gap-s3 before:w-[7.5rem] before:shrink-0 before:text-ink-2 before:content-[attr(data-label)] desk:table-cell desk:px-s4 desk:py-s3 desk:align-top desk:before:content-none ${cell.tone === undefined ? '' : CELL[cell.tone]}`}>
                    {cell.note === undefined ? (
                      cell.text
                    ) : (
                      <span className="flex min-w-0 flex-col">
                        <span>{cell.text}</span>
                        <span className="text-caption text-muted">
                          {/* A note wraps only between its words, never inside one ("KM-TRX-0001"). */}
                          {cell.note.split(' ').map((word, at) => (
                            <Fragment key={at}>
                              {at > 0 && ' '}
                              <span className="whitespace-nowrap">{word}</span>
                            </Fragment>
                          ))}
                        </span>
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A short label as the kiosk's pill, on a soft ground: neutral, the brand's, ok (green), warn, or muted (nothing to say). */
export function Chip({ tone = 'neutral', children }: { readonly tone?: BadgeTone; readonly children: ReactNode }) {
  return <UiBadge tone={tone}>{children}</UiBadge>;
}

/**
 * A summary that opens: to its lines (terms on a voucher), or to more of a
 * form (`children`, open or shut by the page when it holds a problem).
 */
export function Disclosure(props: { readonly summary: string } & ({ readonly lines: readonly string[] } | { readonly open?: boolean; readonly onOpenChange?: (open: boolean) => void; readonly children: ReactNode })) {
  if ('lines' in props)
    return (
      <details className="group">
        <summary className="cursor-pointer font-semibold text-brand">{props.summary}</summary>
        <ul className="m-0 mt-s2 flex list-disc flex-col gap-s1 pl-s5 text-ink-2">
          {props.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
    );
  const { open, onOpenChange } = props;
  return (
    <details
      className="group/more border-t border-line"
      open={open}
      onToggle={(event) => {
        if (event.currentTarget.open !== open) onOpenChange?.(event.currentTarget.open);
      }}
    >
      <summary className="-mx-s2 mt-s2 flex h-control w-fit cursor-pointer list-none items-center gap-s2 rounded-control px-s2 font-semibold text-brand hover:bg-brand-soft focus-visible:outline-2 focus-visible:outline-brand [&::-webkit-details-marker]:hidden">
        <svg viewBox="0 0 16 16" className="size-s4 shrink-0 -rotate-90 group-open/more:rotate-0" aria-hidden="true">
          <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {props.summary}
      </summary>
      <div className="flex flex-col gap-s4 pt-s3">{props.children}</div>
    </details>
  );
}

export type Status = 'live' | 'scheduled' | 'ended';
const STATUS = { live: 'bg-ok-soft text-ok', scheduled: 'bg-brand-soft text-brand', ended: 'bg-sunk text-muted' } as const satisfies Record<Status, string>;

/** Where something stands, as MIX's soft pill: a dot and the word. Live is ok, scheduled the brand, ended muted. */
export function StatusChip({ status, children }: { readonly status: Status; readonly children: string }) {
  return (
    <span className={`inline-flex h-s6 shrink-0 items-center gap-s2 rounded-chip px-s3 text-caption font-semibold whitespace-nowrap ${STATUS[status]}`}>
      <span className="size-s2 rounded-chip bg-current" aria-hidden="true" />
      {children}
    </span>
  );
}

/** One label of a LabelSheet: the QR's value, its label for a screen reader, and the words printed under it. */
export interface Label {
  readonly key: string;
  readonly value: string;
  readonly label: string;
  readonly title: string;
  readonly caption?: string;
}

/**
 * QR labels to cut out, five to a row, on paper only: on screen it shows
 * nothing, and while it is on the page printing prints it alone.
 */
export function LabelSheet({ labels, testId, qrTestId }: { readonly labels: readonly Label[]; readonly testId: string; readonly qrTestId: string }) {
  return createPortal(
    <div data-print-sheet="" data-testid={testId} className="hidden grid-cols-5 gap-s4 bg-surface text-ink print:grid">
      {labels.map((label) => (
        <figure key={label.key} className="flex break-inside-avoid flex-col items-center gap-s1 text-center text-caption">
          <QrCode value={label.value} label={label.label} testId={qrTestId} />
          <figcaption className="flex flex-col">
            <strong className="font-semibold">{label.title}</strong>
            {label.caption !== undefined && <span>{label.caption}</span>}
          </figcaption>
        </figure>
      ))}
    </div>,
    document.body,
  );
}

/**
 * A ticket: its value on a stub (big, condensed numerals) beside its title
 * and terms. `ended` greys it and says why; `action` sits under the terms.
 */
export function TicketCard({
  label,
  value,
  unit,
  title,
  when,
  rules,
  details,
  ended,
  action,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly title: string;
  readonly when: readonly string[];
  readonly rules: readonly string[];
  readonly details?: { readonly summary: string; readonly lines: readonly string[] };
  readonly ended?: string;
  readonly action?: ReactNode;
  readonly testId?: string;
}) {
  const level = useLevel();
  return (
    <article className="grid grid-cols-[6.5rem_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface" data-testid={testId} aria-label={label}>
      <div className={`flex flex-col items-center justify-center gap-s1 border-r border-dashed border-line-strong p-s3 text-center ${ended === undefined ? 'bg-brand-soft text-brand' : 'bg-sunk text-muted'}`} aria-hidden="true">
        <span className="font-figures text-title leading-none font-bold tabular-nums font-stretch-75%">{value}</span>
        <span className="text-caption font-semibold">{unit}</span>
      </div>
      <div className="flex min-w-0 flex-col gap-s2 p-s4">
        <Heading level={level} className={`text-strong font-semibold ${ended === undefined ? 'text-ink' : 'text-ink-2'}`}>
          {title}
        </Heading>
        <p className="flex flex-wrap gap-x-s3 text-ink-2">
          {when.map((part) => (
            <span key={part}>{part}</span>
          ))}
        </p>
        <ul className="m-0 flex list-none flex-wrap gap-x-s3 gap-y-s1 p-0 text-caption text-muted">
          {rules.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
        {details !== undefined && <Disclosure summary={details.summary} lines={details.lines} />}
        {ended !== undefined && <p className="font-semibold text-muted">{ended}</p>}
        {action}
      </div>
    </article>
  );
}
